"use client"

import { useEffect, useState } from "react"
import { Row, Col } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { MapPin, Phone } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { fetchBusinesses } from "../../redux/slices/discountsSlice"

export default function DiscountsPage() {
  const dispatch = useAppDispatch()
  const { businesses = [], isLoading } = useAppSelector((state) => state.discounts)
  const { t } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState<string>("all")

  const filterCategories = [
    { key: "all", label: t("allCategories") || "All" },
    { key: "cafes", label: t("cafes") || "Cafes" },
    { key: "restaurants", label: t("restaurants") || "Restaurants" },
    { key: "gyms", label: t("gyms") || "Gyms" },
    { key: "retail", label: t("retail") || "Retail" },
    { key: "services", label: t("services") || "Services" },
  ]

  // Fetch businesses when component mounts or category changes
  useEffect(() => {
    dispatch(fetchBusinesses(selectedCategory !== "all" ? selectedCategory : undefined))
  }, [dispatch, selectedCategory])

  // Filter businesses by category and active status
  const filteredBusinesses =
    businesses && selectedCategory && selectedCategory !== "all"
      ? businesses.filter((business) => business.category === selectedCategory && business.isActive !== false)
      : businesses.filter((business) => business.isActive !== false) || []

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-white" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="py-4 px-2 px-md-4">
      <div className="d-flex flex-column gap-4 mb-4">
        <div className="d-flex justify-content-between align-items-center" style={{ height: "64px" }}>
          <div className="d-flex align-items-center gap-3">
            <h1 className="fs-2 fw-bold mb-0 text-white">{t("businessDiscounts")}</h1>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
          {filterCategories.map((category) => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className="btn rounded-pill px-3 py-1 fw-semibold border-0"
              style={{
                background: selectedCategory === category.key ? "rgba(255, 255, 255, 0.95)" : "transparent",
                color: selectedCategory === category.key ? "#4c6ef5" : "white",
                border: selectedCategory === category.key ? "none" : "2px solid rgba(255, 255, 255, 0.3)",
                transition: "all 0.3s ease",
                fontSize: "14px",
              }}
              onMouseEnter={(e) => {
                if (selectedCategory !== category.key) {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.5)"
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory !== category.key) {
                  e.currentTarget.style.background = "transparent"
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)"
                }
              }}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      <Row className="g-4">
        {filteredBusinesses.map((business) => (
          <Col key={business.id} xs={12} xl={6}>
            <div
              className="rounded-4 position-relative overflow-hidden d-flex flex-column"
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                padding: "24px 24px 0 24px",
                minHeight: "320px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "none"
              }}
            >
              <img
                src="/top-left.svg"
                alt=""
                className="position-absolute"
                style={{
                  top: 0,
                  right: 0,
                  width: "120px",
                  height: "120px",
                  transform: "rotate(90deg)",
                  pointerEvents: "none",
                }}
              />

              <div className="d-flex align-items-start gap-4 mb-4">
                {/* Business logo */}
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "100px",
                    height: "100px",
                    background: "rgba(255, 255, 255, 0.95)",
                    padding: "12px",
                  }}
                >
                  {business.logo ? (
                    <img
                      src={business.logo || "/placeholder.svg"}
                      alt={business.name}
                      style={{
                        maxHeight: "76px",
                        maxWidth: "76px",
                        objectFit: "contain",
                        border: "none",
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: "76px",
                        height: "76px",
                        background: "#e9ecef",
                        fontSize: "28px",
                        fontWeight: "bold",
                        color: "#6c757d",
                        border: "none",
                      }}
                    >
                      {business.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Business info */}
                <div className="flex-grow-1 pt-2">
                  <h3 className="fs-3 fw-bold mb-2 text-white">{business.name}</h3>
                  <p className="fs-5 text-white opacity-75 mb-0">{t(business.category)}</p>
                </div>
              </div>

              {/* Business info */}
              <div className="mb-4 flex-grow-1">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-md-between gap-2 gap-md-0">
                  <div className="d-flex align-items-center gap-3">
                    <MapPin className="text-white opacity-75 flex-shrink-0" size={20} />
                    <span className="text-white opacity-90 fs-6">{business.address}</span>
                  </div>
                  {business.phone && (
                    <div className="d-flex align-items-center gap-3">
                      <Phone className="text-white opacity-75 flex-shrink-0" size={20} />
                      <span className="text-white opacity-90 fs-6">{business.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Discount banner */}
              <div
                className="text-center mt-auto"
                style={{
                  background: "#000078",
                  backdropFilter: "blur(5px)",
                  margin: "0 -24px",
                  padding: "20px 24px",
                  borderBottomLeftRadius: "1rem",
                  borderBottomRightRadius: "1rem",
                }}
              >
                <span className="fs-6 fs-md-5 fw-bold text-white">{business.discount}</span>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {filteredBusinesses.length === 0 && (
        <div
          className="d-flex justify-content-center align-items-center rounded-4 p-5 mt-4"
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "2px dashed rgba(255, 255, 255, 0.3)",
          }}
        >
          <p className="text-white opacity-75 mb-0">{t("noBusinessesFound")}</p>
        </div>
      )}
    </div>
  )
}
